import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getResourcePath, trans } from '@spotahome/soyuz/client';

import { addClass, removeClass } from '@utils/classHandler';
import Fade from '@components/Fade';
import TextBlock from '@components/TextBlock';

import './PhoneScrollerList.scss';

const TIMEOUT_FIRST_SETUP_LOAD = 1000;

const PhoneScrollerList = ({ scrollerList, titleContent, backgroundColor }) => {
  const fixedContainerRef = useRef();
  const sessionRef = useRef();
  const prevScrollY = useRef(0);
  const [currentItem, setCurrentItem] = useState(-1);
  const currentItemRef = useRef(currentItem);
  currentItemRef.current = currentItem;

  const fixedContainerCN = classNames(
    'phone-scroller-list__fixed-container',
    backgroundColor !== 'white'
      ? `phone-scroller-list__fixed-container--background-${backgroundColor}`
      : ''
  );

  // TODO: include variable to check if item was already loaded
  const textListRef = Array.from({ length: scrollerList.length }, () =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useRef()
  );

  const imageFrameName =
    backgroundColor !== 'white'
      ? `phone-frame-${backgroundColor}`
      : 'phone-frame';

  const frameImageEl = (
    <img
      src={getResourcePath(`/images/phone-scroller/${imageFrameName}.png`)}
      srcSet={`${getResourcePath(
        `/images/phone-scroller/${imageFrameName}-2x.png`
      )} 2x`}
      alt="media-phone-frame"
    />
  );

  const checkChangeItem = fixedContainerHeight => {
    const currentScrollY = window.scrollY;
    if (prevScrollY.current < currentScrollY) {
      if (currentItemRef.current < textListRef.length - 1) {
        const currentTextRect = textListRef[
          currentItemRef.current + 1
        ].current.getBoundingClientRect();
        if (window.innerHeight > currentTextRect.top) {
          setCurrentItem(currentItemRef.current + 1);
        }
      } else if (currentItemRef.current === textListRef.length - 1) {
        const currentTextRect = textListRef[
          currentItemRef.current
        ].current.getBoundingClientRect();
        if (window.innerHeight > currentTextRect.bottom) {
          setCurrentItem(currentItemRef.current + 1);
        }
      }
    } else if (prevScrollY.current > currentScrollY) {
      if (currentItemRef.current > 0) {
        const currentTextRect = textListRef[
          currentItemRef.current - 1
        ].current.getBoundingClientRect();
        if (fixedContainerHeight < currentTextRect.top) {
          setCurrentItem(currentItemRef.current - 1);
        }
      } else if (currentItemRef.current === 0) {
        const currentTextRect = textListRef[
          currentItemRef.current
        ].current.getBoundingClientRect();
        if (window.innerHeight < currentTextRect.top) {
          setCurrentItem(currentItemRef.current - 1);
        }
      }
    }

    prevScrollY.current = currentScrollY;
  };

  const processScroll = useCallback(() => {
    const fixedContainerEl = fixedContainerRef.current;
    const fixedContainerHeight = fixedContainerEl.getBoundingClientRect()
      .height;
    const sessionRect = sessionRef.current.getBoundingClientRect();

    if (sessionRect.bottom < fixedContainerHeight) {
      fixedContainerEl.style.bottom = 0;
      fixedContainerEl.style.top = 'auto';
    } else {
      fixedContainerEl.style.bottom = 'auto';
      fixedContainerEl.style.top = 0;
    }

    if (sessionRect.top <= 0 && sessionRect.bottom > fixedContainerHeight) {
      addClass(fixedContainerEl, 'phone-scroller-list--fix-top');
      checkChangeItem(fixedContainerHeight);
    } else {
      if ([-1, scrollerList.length].indexOf(currentItemRef.current) > -1) {
        if (sessionRect.top > 0) {
          setCurrentItem(-1);
        } else {
          setCurrentItem(scrollerList.length);
        }
      }
      removeClass(fixedContainerEl, 'phone-scroller-list--fix-top');
    }
  });

  const setupPageSize = useCallback(() => {
    const fixedContainerHeight = fixedContainerRef.current.getBoundingClientRect()
      .height;
    const heightTextBlocks = window.innerHeight - fixedContainerHeight;
    let currentItemSetupPage = -1;
    textListRef.forEach((el, index) => {
      const textEl = el.current;
      textEl.style.height = `${heightTextBlocks}px`;
      if (textEl.getBoundingClientRect().top < window.innerHeight) {
        currentItemSetupPage = index;
      }
    });
    if (
      sessionRef.current.getBoundingClientRect().bottom < window.innerHeight
    ) {
      currentItemSetupPage = textListRef.length;
    }
    setCurrentItem(currentItemSetupPage);
    processScroll();
  });

  useEffect(() => {
    window.addEventListener('scroll', processScroll, { passive: true });
    window.addEventListener('resize', setupPageSize, { passive: true });
    window.addEventListener('orientationchange', setupPageSize, {
      passive: true
    });
    setTimeout(() => {
      setupPageSize();
    }, TIMEOUT_FIRST_SETUP_LOAD);
    return () => {
      window.removeEventListener('scroll', processScroll);
      window.removeEventListener('resize', setupPageSize);
      window.removeEventListener('orientationchange', setupPageSize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="phone-scroller-list" ref={sessionRef}>
      <div className="phone-scroller-list__dummy-fixed-container" />
      <div className={fixedContainerCN} ref={fixedContainerRef}>
        <div className="phone-scroller-list__title-wrapper">{titleContent}</div>
        <div className="phone-scroller-list__image-wrapper">
          <div className="phone-scroller-list__dummy-image">{frameImageEl}</div>
          <div className="phone-scroller-list__image-item phone-scroller-list__image-item--active-frame">
            {frameImageEl}
          </div>
          <Fade show={currentItem < 0 || currentItem >= scrollerList.length}>
            <img
              src={getResourcePath(
                '/images/phone-scroller/media-sah-logo-1x.png'
              )}
              srcSet={`${getResourcePath(
                '/images/phone-scroller/media-sah-logo-2x.png'
              )} 2x`}
              alt={trans('phone.scroller.list.initial.image.alt')}
              className="phone-scroller-list__image-item"
            />
          </Fade>
          {scrollerList.map((item, index) => (
            <div
              key={item.title}
              className={`phone-scroller-list__image-item ${
                currentItem === index
                  ? 'phone-scroller-list__image-item--active'
                  : ''
              }`}
            >
              <Fade show={currentItem === index} type="fade">
                {item.renderFrameContent()}
              </Fade>
            </div>
          ))}
        </div>
      </div>
      <div className="phone-scroller-list__text-wrapper">
        {scrollerList.map((item, index) => (
          <div
            key={item.title}
            className="phone-scroller-list__text-item"
            ref={textListRef[index]}
          >
            <TextBlock
              title={item.title}
              content={item.titleText}
              alignTo="center"
              titleTag="h3"
              titleSize="medium"
              contentSize="medium"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

PhoneScrollerList.propTypes = {
  scrollerList: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      titleText: PropTypes.string,
      renderFrameContent: PropTypes.func
    })
  ),
  titleContent: PropTypes.node,
  backgroundColor: PropTypes.oneOf(['white', 'cloud', 'baby-blue'])
};

PhoneScrollerList.defaultProps = {
  titleContent: null,
  backgroundColor: 'white',
  scrollerList: []
};

export default PhoneScrollerList;
